import { normalizePartKind, placementDepth } from "@/home/part-kind";
import { imageDataUrlForDisplay } from "@/home/image-data-url";
import { effectivePlacementDepthBias } from "@/home/placement";
import {
  HOME_DEPTH_BIAS_STEP,
  type HomePartDefinition,
  type HomePlacement,
  type HomeRoomData,
} from "@/home/storage";

export type HomeEditLayerPanelOptions = {
  data: HomeRoomData;
  selectedPlacementId: string | null;
  onSelectPlacement: (placementId: string) => void;
  onAdjustDepthBias?: (placementId: string, delta: number) => void;
};

type PlacementLayerRow = {
  placement: HomePlacement;
  part: HomePartDefinition;
  sortDepth: number;
};

type LayerGroup = {
  partId: string;
  part: HomePartDefinition;
  rows: PlacementLayerRow[];
  maxDepth: number;
};

function layerDepthControls(placementId: string): string {
  return `
    <span class="home-edit-layer-depth-controls" role="group" aria-label="ปรับชั้น">
      <button
        type="button"
        class="home-edit-layer-depth-btn"
        data-placement-id="${escapeHtml(placementId)}"
        data-layer-delta="${-HOME_DEPTH_BIAS_STEP}"
        title="ชั้นหลัง"
        aria-label="ชั้นหลัง"
      >▼</button>
      <button
        type="button"
        class="home-edit-layer-depth-btn"
        data-placement-id="${escapeHtml(placementId)}"
        data-layer-delta="${HOME_DEPTH_BIAS_STEP}"
        title="ชั้นหน้า"
        aria-label="ชั้นหน้า"
      >▲</button>
    </span>`;
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function buildLayerGroups(data: HomeRoomData): LayerGroup[] {
  const partById = new Map(data.parts.map((p) => [p.id, p]));
  const rows: PlacementLayerRow[] = [];

  for (const placement of data.placements) {
    const part = partById.get(placement.partId);
    if (!part) continue;
    const kind = normalizePartKind(part.kind);
    const sortDepth = placementDepth(
      kind,
      placement.gridY,
      part.cellsH,
      effectivePlacementDepthBias(part, placement),
    );
    rows.push({ placement, part, sortDepth });
  }

  const byPart = new Map<string, PlacementLayerRow[]>();
  for (const row of rows) {
    const list = byPart.get(row.part.id) ?? [];
    list.push(row);
    byPart.set(row.part.id, list);
  }

  const groups: LayerGroup[] = [];
  for (const [partId, partRows] of byPart) {
    const part = partRows[0]!.part;
    const sorted = [...partRows].sort((a, b) => b.sortDepth - a.sortDepth);
    groups.push({
      partId,
      part,
      rows: sorted,
      maxDepth: sorted[0]?.sortDepth ?? 0,
    });
  }

  groups.sort((a, b) => b.maxDepth - a.maxDepth);
  return groups;
}

export function renderHomeEditLayerPanel(
  listHost: HTMLElement,
  options: HomeEditLayerPanelOptions,
): void {
  const { data, selectedPlacementId, onSelectPlacement } = options;
  const groups = buildLayerGroups(data);

  if (groups.length === 0) {
    listHost.innerHTML =
      '<p class="home-edit-layer-empty hint">ยังไม่มีชิ้นวางในฉาก</p>';
    return;
  }

  const html = groups
    .map((group) => {
      const thumb = imageDataUrlForDisplay(group.part.imageDataUrl);
      const count = group.rows.length;
      const headSelected =
        count === 1 && group.rows[0]!.placement.id === selectedPlacementId;

      const items = group.rows
        .map((row) => {
          const selected = row.placement.id === selectedPlacementId;
          const bias = effectivePlacementDepthBias(row.part, row.placement);
          const pos = `${row.placement.gridX + 1},${row.placement.gridY + 1}`;
          const single = count === 1;
          if (single) return "";
          return `
            <div class="home-edit-layer-item-row${selected ? " home-edit-layer-item-row--selected" : ""}">
              <button
                type="button"
                class="home-edit-layer-item"
                data-placement-id="${escapeHtml(row.placement.id)}"
                title="${escapeHtml(group.part.name)} · ชั้น ${bias} · ตำแหน่ง ${pos}"
              >
                <span class="home-edit-layer-item-depth" aria-hidden="true">${bias}</span>
                <span class="home-edit-layer-item-pos">${escapeHtml(pos)}</span>
              </button>
              ${layerDepthControls(row.placement.id)}
            </div>`;
        })
        .join("");

      const singleRow = count === 1 ? group.rows[0]! : null;
      const headPlacementId = singleRow?.placement.id ?? "";
      const headBias = singleRow
        ? effectivePlacementDepthBias(singleRow.part, singleRow.placement)
        : 0;

      return `
        <section class="home-edit-layer-group" aria-label="${escapeHtml(group.part.name)}">
          <div class="home-edit-layer-group-head-row${headSelected ? " home-edit-layer-group-head-row--selected" : ""}">
            <button
              type="button"
              class="home-edit-layer-group-head"
              ${count === 1 ? `data-placement-id="${escapeHtml(headPlacementId)}"` : ""}
              ${count > 1 ? "disabled" : ""}
              title="${escapeHtml(group.part.name)}${count === 1 ? ` · ชั้น ${headBias}` : ""}"
            >
              <img class="home-edit-layer-thumb" src="${escapeHtml(thumb)}" alt="" width="28" height="28" decoding="async" />
              <span class="home-edit-layer-name">${escapeHtml(group.part.name)}</span>
              ${count > 1 ? `<span class="home-edit-layer-count">×${count}</span>` : `<span class="home-edit-layer-depth">${headBias}</span>`}
            </button>
            ${count === 1 && headPlacementId ? layerDepthControls(headPlacementId) : ""}
          </div>
          ${count > 1 ? `<div class="home-edit-layer-items">${items}</div>` : ""}
        </section>`;
    })
    .join("");

  listHost.innerHTML = html;

  listHost.querySelectorAll<HTMLButtonElement>("[data-placement-id]").forEach((btn) => {
    if (btn.classList.contains("home-edit-layer-depth-btn")) return;
    btn.addEventListener("click", () => {
      const id = btn.dataset.placementId;
      if (!id) return;
      onSelectPlacement(id);
    });
  });

  listHost.querySelectorAll<HTMLButtonElement>(".home-edit-layer-depth-btn").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.stopPropagation();
      const id = btn.dataset.placementId;
      const delta = Number(btn.dataset.layerDelta);
      if (!id || !Number.isFinite(delta) || !options.onAdjustDepthBias) return;
      options.onAdjustDepthBias(id, delta);
    });
  });
}

export function bindHomeEditLayerPanel(
  root: HTMLElement,
  getOptions: () => HomeEditLayerPanelOptions,
): { refresh: () => void } {
  const refresh = () => {
    const listHost = root.querySelector<HTMLElement>("#home-edit-layer-list");
    if (!listHost) return;
    renderHomeEditLayerPanel(listHost, getOptions());
  };
  return { refresh };
}
